import React from "react";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  InvoiceStatus,
  invoiceTypeToText,
  OwesReportItem,
  PaymentType,
  paymentTypeToText,
} from "../../../../models";
import { markPaid, cancelInvoice } from "../../reportsSlice";
import { setShowStaxPaymentModal } from "../../../Payments/paymentsSlice";
import { CancelInvoiceModal } from "../../../Orders/components/CancelInvoiceModal/CancelInvoiceModal";

export interface InvoiceItemProps {
  invoice: OwesReportItem;
}

export const InvoiceItem: React.FC<InvoiceItemProps> = ({ invoice }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const markPaidAPI = useAppSelector((state) => state.reports.markPaidAPI);
  const cancelInvoiceAPI = useAppSelector((state) => state.reports.cancelInvoiceAPI);

  const [selectedPaymentType, setSelectedPaymentType] = React.useState(
    PaymentType.CARD
  );
  const [showCancelModal, setShowCancelModal] = React.useState(false);

  const onViewOrdeClick = () => navigate(`/orders/${invoice.orderId}`);

  const onMarkPaid = () => {
    if (markPaidAPI.loading) return;

    dispatch(
      markPaid({
        id: invoice.invoiceId,
        paymentType: selectedPaymentType,
        status: InvoiceStatus.PAID,
      })
    );
  };

  const onCancelInvoice = () => {
    if (markPaidAPI.loading || cancelInvoiceAPI.loading) return;
    setShowCancelModal(true);
  };

  const onConfirmCancel = () => {
    dispatch(
      cancelInvoice({
        id: invoice.invoiceId,
        paymentType: selectedPaymentType,
        status: InvoiceStatus.CANCELED,
      })
    );
    setShowCancelModal(false);
  };

  const onCloseCancelModal = () => {
    setShowCancelModal(false);
  };

  const onChargeCard = () => {
    dispatch(setShowStaxPaymentModal(invoice));
  };

  const handlePaymentTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedPaymentType(event.target.value as PaymentType);
  };

  return (
    <div className="invoice-container" key={invoice.invoiceId}>
      <div className="info-container">
        <div className="info-item">
          <div className="info-label">Name:</div>
          <div className="info-value">{invoice.name}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Type:</div>
          <div className="info-value">{invoiceTypeToText[invoice.type]}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Total:</div>
          <div className="info-value">{`$${invoice.total}`}</div>
        </div>

        <div className="view-order-button" onClick={onViewOrdeClick}>
          View order
        </div>
      </div>

      <div className="vertical-divider" />

      <div className="actions-container">
        <div className="button charge-card-button" onClick={onChargeCard}>
          Charge Card
        </div>

        <select
          value={selectedPaymentType}
          onChange={handlePaymentTypeChange}
          className="payment-type-select"
        >
          {Object.values(PaymentType)
            .filter((type) => type !== PaymentType.ACH)
            .map((type) => (
              <option key={type} value={type}>
                {paymentTypeToText[type]}
              </option>
            ))}
        </select>

        <div className="button" onClick={onMarkPaid}>
          Mark paid
        </div>

        <div className="button" onClick={onCancelInvoice}>
          {cancelInvoiceAPI.loading ? (
            <Oval color="#FFFFFF" height={16} width={16} secondaryColor="#FBD20A" />
          ) : (
            "Cancel"
          )}
        </div>
      </div>

      <CancelInvoiceModal
        isOpen={showCancelModal}
        onClose={onCloseCancelModal}
        onConfirm={onConfirmCancel}
        loading={cancelInvoiceAPI.loading}
        invoiceType={invoiceTypeToText[invoice.type]}
        invoiceAmount={invoice.total}
      />
    </div>
  );
};
